"use client"

import { heroTypes } from "@/data/hero-types"
import { setPlayerPrefs, usePlayerPrefs } from "@/lib/player-prefs"
import { usePlayerStats } from "@/lib/player-stats"
import { CheckIcon } from "@/components/icons/pixel-icons"
import { MenuDetailHeading, MenuGroup } from "../menu-primitives"

const HeroOptionRow = ({
  title,
  subtitle,
  portrait,
  trailing,
  isSelected,
  onSelect,
}: {
  title: string
  subtitle: string
  portrait?: string
  trailing?: string
  isSelected: boolean
  onSelect: () => void
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={isSelected}
    onClick={onSelect}
    className={`w-full text-left flex items-center gap-3 px-4 py-3 min-h-[64px] transition-colors ${
      isSelected ? "bg-amber-900/25" : "hover:bg-white/5"
    }`}
  >
    <span className="shrink-0 w-11 h-11 rounded-lg overflow-hidden bg-black/40 border border-amber-600/30 flex items-center justify-center">
      {portrait ? (
        <img
          src={portrait}
          alt=""
          className="w-full h-full object-cover"
          style={{ imageRendering: "pixelated" }}
        />
      ) : (
        <span className="text-lg font-bold text-amber-400">?</span>
      )}
    </span>
    <span className="min-w-0 flex-1">
      <span
        className={`block text-sm font-bold leading-tight ${
          isSelected ? "text-amber-200" : "text-gray-100"
        }`}
      >
        {title}
      </span>
      <span className="block text-xs leading-snug text-gray-400 mt-0.5">{subtitle}</span>
      {trailing && (
        <span className="block text-[11px] leading-snug text-gray-500 mt-0.5">{trailing}</span>
      )}
    </span>
    <span className={`shrink-0 ${isSelected ? "text-amber-300" : "text-transparent"}`}>
      <CheckIcon size={18} />
    </span>
  </button>
)

/**
 * Which hero a new run starts with.
 *
 * "Random" stays the default because it is what the game has always done and it
 * keeps runs varied; pinning a hero is opt-in for players who want to practise
 * or chase a hero-specific record.
 */
export const HeroesSection = () => {
  const prefs = usePlayerPrefs()
  const stats = usePlayerStats()

  const heroes = Object.values(heroTypes)
  const selected = prefs.preferredHero
  const choose = (value: string) => setPlayerPrefs({ preferredHero: value })

  return (
    <div>
      <MenuDetailHeading
        title="Heroes"
        blurb="Choose who drops in when you start a new run. This does not affect a run already in progress."
      />

      <div role="radiogroup" aria-label="Starting hero">
        <MenuGroup title="Starting hero">
          <HeroOptionRow
            title="Random hero"
            subtitle="Re-rolled at the start of every run"
            isSelected={selected === "random"}
            onSelect={() => choose("random")}
          />
          {heroes.map((hero) => {
            const heroStat = stats.heroes[hero.id]
            return (
              <HeroOptionRow
                key={hero.id}
                title={hero.name}
                subtitle={hero.description}
                portrait={hero.portrait}
                trailing={
                  heroStat
                    ? `${heroStat.runs} run${heroStat.runs === 1 ? "" : "s"} · best ${heroStat.bestScore.toLocaleString()}`
                    : "Not played yet"
                }
                isSelected={selected === hero.id}
                onSelect={() => choose(hero.id)}
              />
            )
          })}
        </MenuGroup>
      </div>
    </div>
  )
}
